import { Loader2, Send, CalendarClock, RefreshCw, ChevronDown, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { ordinal } from './capabilityAnalyticsUtils';

export function ScheduleDialog({
  open, onOpenChange, form, setForm, saving, sending, onSave, onSendNow,
  history, historyOpen, setHistoryOpen,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cap-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-cyan-600" /> Schedule monthly update
          </DialogTitle>
          <DialogDescription className="sr-only">Configure recipients, send-day, PDF attachment and view delivery history for the monthly capability update.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Email this update monthly</p>
              <p className="text-[11px] text-slate-500">Auto-captures a snapshot &amp; emails the fresh AI update.</p>
            </div>
            <Switch checked={form.enabled}
              onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))}
              data-testid="cap-schedule-enabled" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Recipients</Label>
            <textarea value={form.recipients}
              onChange={(e) => setForm(f => ({ ...f, recipients: e.target.value }))}
              placeholder="exec@company.com, hrbp@company.com" rows={2}
              className="w-full mt-1 text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
              data-testid="cap-schedule-recipients" />
            <p className="text-[10px] text-slate-400 mt-0.5">Comma- or newline-separated email addresses.</p>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-xs font-semibold text-slate-600 whitespace-nowrap">Send on the</Label>
            <Select value={String(form.day_of_month)}
              onValueChange={(v) => setForm(f => ({ ...f, day_of_month: Number(v) }))}>
              <SelectTrigger className="h-8 w-[110px] text-sm" data-testid="cap-schedule-day">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 5, 7, 10, 15, 28].map(d => <SelectItem key={d} value={String(d)}>{ordinal(d)}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">of each month</span>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Note (optional)</Label>
            <input value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="A line for the recipients…"
              className="w-full mt-1 text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
              data-testid="cap-schedule-note" />
          </div>

          {/* Attach annual report PDF */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-cyan-600" /> Attach annual report PDF
                </p>
                <p className="text-[11px] text-slate-500">Include the full board-ready capability report.</p>
              </div>
              <Switch checked={form.attach_pdf}
                onCheckedChange={(v) => setForm(f => ({ ...f, attach_pdf: v }))}
                data-testid="cap-schedule-attach-pdf" />
            </div>
            {form.attach_pdf && (
              <div className="flex items-center gap-2 pt-1">
                <Label className="text-xs font-semibold text-slate-600 whitespace-nowrap">Attach</Label>
                <Select value={form.attach_mode}
                  onValueChange={(v) => setForm(f => ({ ...f, attach_mode: v }))}>
                  <SelectTrigger className="h-8 w-[190px] text-sm" data-testid="cap-schedule-attach-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">Every month</SelectItem>
                    <SelectItem value="december">December only (year-end)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Delivery history (audit trail) */}
          <div className="border-t border-slate-100 pt-3">
            <button onClick={() => setHistoryOpen(v => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800"
              data-testid="cap-schedule-history-toggle">
              <RefreshCw className="h-3 w-3" /> Delivery history
              <span className="text-slate-400 font-normal">({history.length})</span>
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', historyOpen ? '' : '-rotate-90')} />
            </button>
            {historyOpen && (
              <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto" data-testid="cap-schedule-history">
                {history.length === 0 ? (
                  <p className="text-[11px] text-slate-400 py-2">No updates have been sent yet.</p>
                ) : history.map(d => (
                  <div key={d.id} className="rounded-lg border border-slate-200 p-2" data-testid={`cap-history-${d.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={cn('text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide',
                        d.source === 'auto' ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-600')}>
                        {d.source === 'auto' ? 'Scheduled' : 'Manual'}
                      </span>
                      <span className="text-xs text-slate-600">{d.sent_at ? new Date(d.sent_at).toLocaleString() : ''}</span>
                      <span className={cn('ml-auto text-[11px] font-semibold tabular-nums',
                        d.sent_count === d.recipient_count ? 'text-emerald-700' : 'text-amber-700')}>
                        {d.sent_count}/{d.recipient_count} delivered
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(d.recipients || []).map((r, i) => (
                        <span key={i} title={r.detail || r.reason || (r.sent ? 'Delivered' : 'Failed')}
                          className={cn('text-[10px] px-1.5 py-0.5 rounded-full border',
                            r.sent ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200')}>
                          {r.sent ? '✓' : '✕'} {r.email}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5"
            onClick={onSendNow} disabled={sending || saving}
            data-testid="cap-schedule-send-now">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send now
          </Button>
          <Button size="sm" className="h-9 text-xs gap-1.5 bg-cyan-600 hover:bg-cyan-700"
            onClick={onSave} disabled={saving}
            data-testid="cap-schedule-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />}
            Save schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
